import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import api from '../../../../utils/api';
import { Modal, Button, Field, Input, Textarea, Select, Segmented, errMsg, money, todayIso } from '../../kit';
import ParticipantPicker from '../ParticipantPicker';
import { useScMe } from '../participants';

// A manual claim line: anything claimed that is not time (a fee, kilometres, a report), by
// quantity or in hours, for one participant on one date. It waits as "pending" and joins the next
// batch through the same checks as time. line: an existing pending one to edit (from the preview).
const CLAIM_TYPES = [
  { value: '', label: 'Direct service' }, { value: 'NF2F', label: 'Non-face-to-face' }, { value: 'TRAN', label: 'Provider travel' },
  { value: 'REPW', label: 'NDIA-requested report' }, { value: 'THLT', label: 'Telehealth' }, { value: 'CANC', label: 'Short-notice cancellation' },
];
const CANC = [
  { value: 'NSDH', label: 'No show due to health reason (NSDH)' }, { value: 'NSDF', label: 'No show due to family issues (NSDF)' },
  { value: 'NSDT', label: 'No show due to transport (NSDT)' }, { value: 'NSDO', label: 'Other (NSDO)' },
];
const NONE = '__direct__';
const blank = () => ({ coordination: null, service_date: todayIso(), support_item: '07_002_0106_8_3', claim_type: '', cancellation_reason: '', unit: 'each', quantity: '1', unit_price: '', description: '' });

export default function ManualLineModal({ open, line, onClose, onSaved }) {
  const me = useScMe({ skip: !open });
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!open) return;
    setErr(null);
    setF(line ? {
      coordination: { id: line.coordination_id, name: line.client_name || '' }, service_date: String(line.service_date).slice(0, 10), support_item: line.support_item,
      claim_type: line.claim_type || '', cancellation_reason: line.cancellation_reason || '', unit: line.unit || 'each',
      quantity: String(line.quantity ?? ''), unit_price: String(line.rate ?? ''), description: line.description || '',
    } : blank());
  }, [open, line]);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const amount = useMemo(() => Math.round((Number(f.quantity) || 0) * (Number(f.unit_price) || 0) * 100) / 100, [f.quantity, f.unit_price]);

  const save = async () => {
    setErr(null);
    if (!f.coordination) { setErr('Choose the participant.'); return; }
    const body = {
      coordination_id: f.coordination.id, service_date: f.service_date, support_item: f.support_item.trim(), claim_type: f.claim_type,
      cancellation_reason: f.claim_type === 'CANC' ? f.cancellation_reason : '', unit: f.unit, quantity: Number(f.quantity), unit_price: Number(f.unit_price), description: f.description,
    };
    setSaving(true);
    try {
      if (line && line.extra_id) await api.patch(`/sc/claims/extras/${line.extra_id}`, body);
      else await api.post('/sc/claims/extras', body);
      toast.success(line ? 'Manual line saved' : 'Manual line added. It joins the next batch.');
      onSaved(); onClose();
    } catch (e) { setErr(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} width="lg" title={line ? 'Edit manual claim line' : 'Add a claim line'}
      sub="For something to claim that isn't logged time: a fee, kilometres or a report. It goes through the same checks and into the next batch."
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button icon={line ? undefined : Plus} loading={saving} onClick={save}>{line ? 'Save' : 'Add line'}</Button></>}>
      <div className="space-y-4">
        <Field label="Participant" required>
          <ParticipantPicker value={f.coordination} me={me} today={todayIso()} onChange={(p) => set({ coordination: p ? { id: p.id, name: p.name } : null })} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Service date" required><Input type="date" max={todayIso()} value={f.service_date} onChange={(e) => set({ service_date: e.target.value })} /></Field>
          <Field label="Support item" required hint="The NDIS support item number, as in the participant's plan.">
            <Input value={f.support_item} onChange={(e) => set({ support_item: e.target.value })} className="tabular-nums" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Claim type">
            <Select value={f.claim_type || NONE} onChange={(v) => set({ claim_type: v === NONE ? '' : v })} options={CLAIM_TYPES.map((c) => ({ value: c.value || NONE, label: c.label }))} />
          </Field>
          {f.claim_type === 'CANC' && (
            <Field label="Cancellation reason" required>
              <Select value={f.cancellation_reason} onChange={(v) => set({ cancellation_reason: v })} options={CANC} placeholder="Choose the reason" />
            </Field>
          )}
        </div>
        <div>
          <p className="text-[12.5px] font-semibold text-slate-700 mb-1.5">Claimed by</p>
          <Segmented value={f.unit} onChange={(v) => set({ unit: v })} options={[{ value: 'each', label: 'Quantity' }, { value: 'hour', label: 'Hours' }]} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 items-end">
          <Field label={f.unit === 'hour' ? 'Hours' : 'Quantity'} required hint={f.unit === 'hour' ? 'For example 0.5 for 30 minutes.' : 'For example 12 for 12 km.'}>
            <Input type="number" min="0.01" step="0.01" value={f.quantity} onChange={(e) => set({ quantity: e.target.value })} className="tabular-nums" />
          </Field>
          <Field label={f.unit === 'hour' ? 'Rate per hour' : 'Unit price'} required>
            <Input type="number" min="0.01" step="0.01" value={f.unit_price} onChange={(e) => set({ unit_price: e.target.value })} className="tabular-nums" placeholder="0.00" />
          </Field>
          <div className="col-span-2 sm:col-span-1 rounded-xl bg-slate-50 ring-1 ring-inset ring-slate-200 px-3 py-2">
            <p className="text-[12px] text-slate-500">Amount</p>
            <p className="text-[18px] font-semibold tabular-nums text-slate-900">{money(amount)}</p>
          </div>
        </div>
        <Field label="Description" hint="Shown to your team; the invoice uses the support item name.">
          <Textarea rows={2} value={f.description} onChange={(e) => set({ description: e.target.value })} placeholder="For example: Travel to the plan review, 12 km" />
        </Field>
        {err && <p className="text-[13px] text-rose-600" role="alert">{err}</p>}
      </div>
    </Modal>
  );
}
